import React from 'react';
import { useState, useEffect } from 'react';
import { Alert, Card, CardContent, TextField, MenuItem, Select } from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

const schema = yup.object().shape({
  countryName: yup.string().required("Country is required"),
  stateName: yup
    .string()
    .required('State is required')
    .min(2, 'State must be at least 2 characters long')
    .max(50, 'State must be less than 50 characters')
    .trim()
});

const AddUpdateStateModal = ({ refetch, onClose, stateId = null, initialData = null }) => {
  const dispatch = useDispatch();
  
  // Form states
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(initialData?.country_name || '');
  const [stateName, setStateName] = useState(initialData?.state_name || '');

  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    countryName: '',
    stateName: ''
  });

  const validateForm = async () => {
    try {
      await schema.validate({ 
        countryName: selectedCountry,
        stateName
      }, { abortEarly: false });
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ countryName: '', stateName: '' });

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const selectedCountryObj = countries.find(country => country.country_name === selectedCountry);
      const payload = {
        countryId: selectedCountryObj?.id,
        stateName: stateName.trim(),
        stateId: stateId || 0,
      };

      const response = await api.post('/admin/CreateUpdateStates', payload);
      
      if (response.data.status === '200') {
        dispatch(showNotification({ 
          message: response.data.message, 
          type: "success" 
        }));
        onClose();
        refetch();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        general: err.message || 'An error occurred while saving'
      }));
      dispatch(showNotification({ 
        message: err.message || 'An error occurred', 
        type: "error" 
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCountries = async () => {
    try {
      const response = await api.get('/admin/getCountry?page=1&limit=100000');
      if (response.data.data) {
        setCountries(response.data.data);
      }
    } catch (err) {
      dispatch(showNotification({ 
        message: 'Failed to fetch countries', 
        type: "error" 
      }));
    }
  };

  useEffect(() => {
    getCountries();
  }, []);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="countryName" className="text-sm font-medium">
              Country
            </label>
            <Select
              id="countryName"
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              placeholder="Select country"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.countryName)}
              className="w-full"
            >
              {countries.map((country) => (
                <MenuItem key={country.id} value={country.country_name}>
                  {country.country_name}
                </MenuItem>
              ))}
            </Select>
            {errors.countryName && (
              <p className="text-sm text-red-500 mt-1">{errors.countryName}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="stateName" className="text-sm font-medium">
              State Name
            </label>
            <TextField
              id="stateName"
              value={stateName}
              onChange={(e) => setStateName(e.target.value)}
              placeholder="Enter state name"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.stateName)}
              className="w-full"
            />
            {errors.stateName && (
              <p className="text-sm text-red-500 mt-1">{errors.stateName}</p>
            )}
          </div>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={stateId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateStateModal;